import { useCallback, useEffect, useState } from "react";
import type { AdminTagReport, TagReportResolution } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { isSafeMode } from "../lib/safe-mode";
import { Button } from "../control/ui/primitives";

/**
 * Review queue for AI-assigned keywords users have flagged as wrong.
 *
 * Two verdicts, and they are not symmetric — the report claims the model got it
 * wrong, so:
 *
 *   Keep    the model was right after all. The association is promoted ai -> admin,
 *           which retires its badge and takes it out of reporting range for good.
 *   Remove  the model was wrong. The keyword comes off this clip.
 *
 * Both close every open report on the pair. Rows are grouped per clip+keyword, so a
 * keyword ten people flagged is one decision, not ten.
 */
export function TagReports() {
  const [reports, setReports] = useState<AdminTagReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { reports: list } = await adminApi.listTagReports();
      setReports(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function resolve(r: AdminTagReport, resolution: TagReportResolution) {
    const id = rowKey(r);
    setBusy(id);
    try {
      const { reports: list } = await adminApi.resolveTagReport(r.clipId, r.tag, resolution);
      setReports(list);
    } catch {
      // Already resolved elsewhere (404) — resync rather than guess.
      void load();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">Mislabelled keywords</h1>
          <p className="text-xs text-zinc-500">
            Keywords GoonerAI assigned that users flagged as wrong. Keep marks one
            admin-verified (badge removed); Remove takes it off the clip.
          </p>
        </div>
        <span className="rounded-full bg-[var(--color-panel-2)] px-2.5 py-1 text-xs font-medium text-zinc-300">
          {reports.length} pending
        </span>
      </header>

      {loading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : reports.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-edge)] p-8 text-center text-sm text-zinc-500">
          Nothing reported. Keywords users flag as mislabelled show up here.
        </div>
      ) : (
        <ul className="space-y-2">
          {reports.map((r) => (
            <li
              key={rowKey(r)}
              className="flex items-center gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-2.5"
            >
              <Thumb report={r} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-zinc-100">
                    {r.displayName}
                  </span>
                  {r.reports > 1 ? (
                    <span className="shrink-0 rounded-full bg-[var(--color-panel-2)] px-1.5 py-0.5 text-[10px] font-medium text-zinc-400">
                      ×{r.reports}
                    </span>
                  ) : null}
                </div>
                {/* The slug, since it's what the keyword actually IS on the clip and
                    the display name can differ from it entirely. */}
                <div className="truncate text-xs text-zinc-500">{r.tag}</div>
                {r.notes.length > 0 ? (
                  <div className="mt-0.5 truncate text-xs text-zinc-400" title={r.notes.join(" · ")}>
                    “{r.notes[0]}”
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => void resolve(r, "keep")}
                  disabled={busy === rowKey(r)}
                  title="The model was right — mark this admin-verified and drop the AI badge"
                >
                  Keep
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void resolve(r, "remove")}
                  disabled={busy === rowKey(r)}
                  title="The model was wrong — take this keyword off the clip"
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A report is identified by the PAIR it targets — there's one decision per
 *  clip+keyword no matter how many people reported it. */
function rowKey(r: AdminTagReport): string {
  return `${r.clipId}::${r.tag}`;
}

/** Small clip preview so the reviewer can judge the keyword without leaving. */
function Thumb({ report }: { report: AdminTagReport }) {
  const cls = "h-14 w-14 shrink-0 rounded-lg border border-[var(--color-edge)] bg-black object-cover";
  const clip = report.clip;
  if (!clip) {
    return (
      <div className={`${cls} flex items-center justify-center text-[10px] text-zinc-600`}>
        gone
      </div>
    );
  }
  if (clip.posterUrl) {
    return <img src={clip.posterUrl} alt="" className={cls} />;
  }
  return (
    <video
      src={clip.url}
      className={cls}
      muted
      loop
      playsInline
      preload="metadata"
      disablePictureInPicture={isSafeMode()}
    />
  );
}
